import React, { createContext, ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

type AppHeaderAndPromptsVisibility = {
	areAppHeaderAndPromptsHidden: boolean;
	/** Adds (+1) or releases (-1) one request to hide them; they stay hidden while any request remains. */
	changeHideRequests: (delta: 1 | -1) => void;
};

/** Whether a full-page view hides the app header (with the collections panel and hints banner) and the onboarding and cookie prompts. */
const AppHeaderAndPromptsVisibilityContext = createContext<AppHeaderAndPromptsVisibility | null>(null);

export function AppHeaderAndPromptsVisibilityProvider({ children }: { children: ReactNode }) {
	const [hideRequestCount, setHideRequestCount] = useState(0);
	const changeHideRequests = useCallback((delta: 1 | -1) => setHideRequestCount((count) => count + delta), []);
	const value = useMemo(
		() => ({ areAppHeaderAndPromptsHidden: hideRequestCount > 0, changeHideRequests }),
		[hideRequestCount, changeHideRequests],
	);
	return (
		<AppHeaderAndPromptsVisibilityContext.Provider value={value}>
			{children}
		</AppHeaderAndPromptsVisibilityContext.Provider>
	);
}

export function useAppHeaderAndPromptsVisibility(): AppHeaderAndPromptsVisibility {
	const value = useContext(AppHeaderAndPromptsVisibilityContext);
	if (!value) {
		throw new Error('useAppHeaderAndPromptsVisibility must be used inside AppHeaderAndPromptsVisibilityProvider');
	}
	return value;
}

/** Hides the app header and prompts while the calling view is mounted and `hidden` holds; applied before paint so they never flash. */
export function useHideAppHeaderAndPrompts(hidden = true) {
	const { changeHideRequests } = useAppHeaderAndPromptsVisibility();
	useLayoutEffect(() => {
		if (!hidden) return undefined;
		changeHideRequests(1);
		return () => changeHideRequests(-1);
	}, [hidden, changeHideRequests]);
}
